import * as joint from '@clientio/rappid';
import { TranslocoService } from '@jsverse/transloco';
import { ComponentConverter } from 'shared/domain';

import {
  ActivityDefinitionDto,
  ComponentDto,
  FlowchartElementType,
  FlowchartLinkDto,
  FlowchartNodeStyleDto,
  FlowchartTextBlockDto,
} from '@treetop/shared/model';

import { BasicNode } from './BasicNode';
import { IFlowchartNode } from './IFlowchartNode';
import { FlowchartSidebarStoreService } from '../../../../../guideline/feature-sidebar/src/lib/infrastructure/flowchart-sidebar-store.service';
import { FlowchartConfig } from '../infrastructure/base/flowchart.config';
import OperationsService from '../infrastructure/operations/operations.service';
import { PortsService } from '../infrastructure/operations/ports.service';
import SubflowService from '../infrastructure/operations/subflow.service';
import { HtmlUtil } from '../util/html.util';

export type FlowchartTextBlockCreateOptions = {
  text?: string;
  id?: string;
  uniqueId?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  customStyling?: FlowchartNodeStyleDto;
  placeholder?: string;
};

export class FlowchartTextBlockNode extends BasicNode<FlowchartTextBlockDto> implements IFlowchartNode {
  constructor(element: FlowchartTextBlockDto) {
    super(element);
  }

  addOutgoingLink(link: FlowchartLinkDto): void {
    this.element.outgoingLinks = this.element.outgoingLinks || [];
    this.element.outgoingLinks.push(link);
  }

  removeOutgoingLink(linkIndex: number): void {
    this.element.outgoingLinks.splice(linkIndex, 1);
  }

  public static create(graph: joint.dia.Graph, options?: FlowchartTextBlockCreateOptions): joint.dia.Cell {
    let textBlockSize = FlowchartConfig.textBlockNode.size;
    if (options.width != null && options.height != null) {
      textBlockSize = { width: options.width, height: options.height };
    }

    const borderColor = options?.customStyling?.borderColor ?? FlowchartConfig.textBlockNode.styles.borderColor;
    const backgroundColor = options?.customStyling?.backgroundColor ?? FlowchartConfig.textBlockNode.styles.backgroundColor;
    const borderStyle = options?.customStyling?.borderStyle ?? FlowchartConfig.textBlockNode.styles.borderStyle;

    const rectangle = new joint.shapes.standard.Rectangle({
      position: { x: options?.x, y: options?.y },
      size: textBlockSize,
      z: 1,
      attrs: {
        body: {
          rx: FlowchartConfig.textBlockNode.styles.borderRadius,
          ry: FlowchartConfig.textBlockNode.styles.borderRadius,
          width: textBlockSize.width,
          height: textBlockSize.height,
          stroke: borderColor,
          fill: backgroundColor,
          strokeDasharray: borderStyle == 'solid' ? '' : borderStyle == 'dashed' ? '5 5' : '2',
        },
      },
    });

    if (options.id) {
      rectangle.set('id', options.id);
    }
    rectangle.attr('body/unique-id', options?.uniqueId ?? rectangle.id);

    rectangle.set('isTextBlock', true);

    PortsService.addPorts(rectangle);

    const foreignObject = this.createTextBlock(
      options?.text,
      options?.x + FlowchartConfig.textBlockNode.offsetLeft,
      options?.y + FlowchartConfig.textBlockNode.offsetTop,
      textBlockSize.width,
      textBlockSize.height,
      options?.placeholder,
    );

    graph.addCell(foreignObject);
    foreignObject.set('isTextBlockContent', true);
    rectangle.embed(foreignObject);

    return rectangle;
  }

  private static createTextBlock(text: string, x: number, y: number, width: number, height: number, placeholder: string) {
    const textBlock = joint.dia.Element.define(
      'example.ForeignObject',
      {
        size: FlowchartConfig.textBlockNode.size,
        position: { x, y },
        z: FlowchartConfig.textBlockNode.zIndex,
        attrs: {
          body: {
            class: 'element--textblock__body',
            width: width - FlowchartConfig.textBlockNode.offsetLeft * 2,
            height: height - FlowchartConfig.textBlockNode.offsetTopWithPadding,
            fill: 'transparent',
          },
          foreignObject: {
            class: 'element--textblock__foreignObject no-margin',
            width: width - FlowchartConfig.textBlockNode.offsetLeft * 2,
            height: height - FlowchartConfig.textBlockNode.offsetTopWithPadding,
            fontFamily: "'Roboto', 'Helvetica Neue', sans-serif",
            fontSize: '14px',
            margin: '8px',
          },
        },
      },
      {
        markup: joint.util.svg`
              <rect @selector="body"/>
              <foreignObject @selector="foreignObject">
                ${text ? HtmlUtil.sanitizeHTMLForSVG(text) : '<span class="element--textblock__placeholder">' + placeholder + '</span>'}
              </foreignObject>
          `,
      },
    );
    return new textBlock();
  }

  public addToGraph(
    graph: joint.dia.Graph,
    paper: joint.dia.Paper,
    operationsService: OperationsService,
    subflowService: SubflowService,
    translocoService?: TranslocoService,
    allowEvents = false,
  ): joint.dia.Cell {
    const textBlockElement = FlowchartTextBlockNode.create(graph, {
      text: this.element.content,
      x: this.element.positionX,
      y: this.element.positionY,
      width: this.element.width,
      height: this.element.height,
      id: this.element.id,
      uniqueId: this.element.uniqueId,
      customStyling: this.element.customStyling,
      placeholder: translocoService.translate(FlowchartConfig.textBlockNode.placeholder),
    });

    graph.addCell(textBlockElement);
    operationsService.addElementTags(this.element);
    return textBlockElement;
  }

  setPropertiesForInit(element: joint.dia.Cell): void {
    super.setPropertiesForInit(element);
    this.element.elementType = FlowchartElementType.FlowchartTextBlock;
  }

  async setPropertiesAfterSearch(
    definition: ComponentDto,
    medicationRequests: ActivityDefinitionDto[],
    componentConverter: ComponentConverter,
    selectedElementView: joint.dia.ElementView,
    translocoService?: TranslocoService,
    flowchartSidebarStoreService?: FlowchartSidebarStoreService,
  ): Promise<void> {
    // Do nothing...no search involved
  }
}
